import React, { useState } from 'react'
import { RegionViewer, PositionAxisTrack } from '@axaou/ui'
import { useRecoilState, useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { Button } from '@gnomad/ui' // Ensure Table is imported
import { QueryState } from '@axaou/ui'

import {
  AncestryGroupCodes,
  variantIdAtom,
  locusMafAtom,
} from '../sharedState'
import { useAppNavigation } from '../hooks/useAppNavigation'
import { geneMacColumnMode, hasGeneEffectEstimate } from '../geneAssociationSemantics'
import { TinySpinner } from '../UserInterface'
import {
  gwasCatalogOptionsAtom,
  membershipFiltersAtom,
  multiAnalysisVariantDetailsAtom,
  multiAnalysisVariantTableFormatAtom,
  sortStateAtom,
  variantFilterAtom,
  variantSearchTextAtom,
} from '../variantState'

import filterVariants from '../VariantList/filterVariants'
import sortItems from '../sortItems'

import { LocusPagePlots } from './LocusPagePlots'
import { GenePageVariantTable } from './GenePageVariantTable'
import { VariantDetails } from './VariantDetails'
import { IndividualVariantResultsTable } from './IndividualVariantResultsTable'
import { ZoomRegion } from './LocusPlotControls'
import GeneBurdenTable from './GeneBurdenTable'
import GenesTrackContainer from './GenesTrackContainer'
import getColumns from '../GeneResults/geneResultColumns'
import {
  GENE_DETAIL_INLINE_GUTTER_PX,
  geneDetailGridContract,
  normalizeRegionViewerWidth,
} from '../geneDetailLayout'

import {
  GeneAssociations,
  AnalysisMetadata,
  VariantJoined,
  GeneModels,
  VariantDataset,
  LocusPlotResponse,
  RegionOverlayResponse,
} from '../types'
import GeneResultsTable from '../GeneResults/GeneResultsTable'
import {
  findVariantById,
  GenomicContext,
  includeSelectedVariant,
  normalizeVariantId,
  variantIdsEqual,
} from './genomicContext'

// Styled Components (unchanged)
const GenePageGridStyles = styled.div<{ $embedded?: boolean }>`
  width: 100%;
  min-width: 0;
  height: ${({ $embedded }) => ($embedded ? 'auto' : '100%')};
  min-height: ${({ $embedded }) => ($embedded ? '0' : '100%')};

  h3 {
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-right: 5px;
  }

  .associations-page-grid {
    width: 100%;
    height: ${({ $embedded }) => ($embedded ? 'auto' : '100%')};
    box-sizing: border-box;
    min-width: 0;
    min-height: 0;
    overflow-y: ${({ $embedded }) => ($embedded ? 'visible' : 'auto')};
    overflow-x: auto;
    padding-inline: ${GENE_DETAIL_INLINE_GUTTER_PX}px;
    padding-bottom: 50px;

    display: grid;

    grid-template-columns: 1fr 1fr;
    grid-auto-rows: ${geneDetailGridContract.intrinsicRowSizing};
    grid-template-areas:
      'sva-title sva-title'
      'plot-controls plot-controls'
      'region-viewer region-viewer'
      'variant-table variant-table';

    justify-content: center;
    align-content: flex-start;
    align-items: flex-start;
  }

  .grid-area-sva-title {
    grid-area: sva-title;
  }

  .grid-area-plot-controls {
    grid-area: plot-controls;
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    gap: 10px;
    flex-wrap: wrap;
    min-width: 0;
    margin-top: 10px;
  }

  .gene-phenotype-title {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  }

  .grid-area-gene-info {
    grid-area: gene-info;
  }

  .grid-area-region-viewer {
    grid-area: region-viewer;
    width: 100%;
    min-width: 0;
  }

  .grid-area-variant-details {
    max-width: 600px;
    grid-area: variant-details;
    width: 90%;
    min-width: 0;
    height: 100%;
  }

  .grid-area-variant-table {
    grid-area: variant-table;
    width: 100%;
    max-width: 100%;
    margin-top: 20px;
    margin-bottom: 20px;
    min-height: 500px;
    overflow-x: auto;
  }

  @media (max-width: 600px) {
    h3 {
      white-space: normal;
    }

    .associations-page-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .grid-area-plot-controls {
      align-items: flex-start;
      flex-direction: column;
    }

    .grid-area-variant-details {
      width: 100%;
      max-width: none;
    }
  }
`

const PageWithVariantDetails = styled(GenePageGridStyles)`
  .associations-page-grid {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas:
      'sva-title sva-title sva-title'
      'plot-controls plot-controls plot-controls'
      'region-viewer region-viewer variant-details'
      'variant-table variant-table variant-table';
  }

  @media (max-width: 600px) {
    .associations-page-grid {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas:
        'sva-title'
        'plot-controls'
        'region-viewer'
        'variant-details'
        'variant-table';
    }
  }
`

const PageWithGeneBurdenDetails = styled(GenePageGridStyles)`
  .associations-page-grid {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas:
      'burden-title burden-title burden-title'
      'burden-table burden-table burden-table'
      'sva-title sva-title sva-title'
      'plot-controls plot-controls plot-controls'
      '${geneDetailGridContract.regionRowWithoutVariantDetails}'
      'variant-table variant-table variant-table';
  }

  .grid-area-burden-title {
    grid-area: burden-title;
  }

  .grid-area-burden-table {
    grid-area: burden-table;
    width: 100%;
    min-width: 0;
    margin-bottom: 20px;
    overflow-x: auto;

    display: grid;
    grid: min-content fit-content / max-content max-content;

    .grid-burden-table-values {
      grid-column: span 3;
    }
  }

  .threshold-legend {
    font-size: 10px;
    justify-self: end;
  }

  @media (max-width: 600px) {
    .associations-page-grid {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas:
        'burden-title'
        'burden-table'
        'sva-title'
        'plot-controls'
        'region-viewer'
        'variant-details'
        'variant-table';
    }
  }
`

const LeftPanel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding-right: 10px;
`

const StrandIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 10px;
  background: #424242;
  color: #fff;
  font-size: 18px;
  line-height: 20px;
  text-align: center;
`

const GwasCatalogDetailsContainer = styled.div`
  margin-bottom: 40px;

  .gwas-details-item {
    strong {
      margin-right: 5px;
    }
    margin-top: 10px;
  }
`

interface GwasCatalogDetailsProps {
  gwasCatalog: any[]
}

const GwasCatalogDetails: React.FC<GwasCatalogDetailsProps> = ({ gwasCatalog }) => {
  return (
    <GwasCatalogDetailsContainer>
      <h2>GWAS Catalog Entries</h2>
      {gwasCatalog.map((item, i) => (
        <div className="gwas-details-item" key={`gwas-catalog-details-${i}`}>
          <div>
            <strong>Trait</strong>
            <span>{item.trait}</span>
          </div>
          <div>
            <strong>Mapped gene</strong>
            <span>{item.mapped_gene}</span>
          </div>
          <div>
            <strong>Study:</strong>
            <span>{item.study}</span>
          </div>
          <div>
            <strong>Author:</strong>
            <span>{item.first_author}</span>
          </div>
          <div>
            <strong>Journal:</strong>
            <span>{item.journal}</span>
          </div>
          <div>
            <strong>Date:</strong>
            <span>{item.date_added}</span>
          </div>
          <div>
            <strong>Pubmed ID:</strong>
            <span>
              <a target="_blank" rel="noopener noreferrer" href={`https://${item.link}`}>
                {item.pubmed_id}
              </a>
            </span>
          </div>
        </div>
      ))}
    </GwasCatalogDetailsContainer>
  )
}

const parseRegionId = (regionId: string) => {
  if (!regionId) {
    throw new Error('Region ID is missing')
  }

  const [contig, start, stop] = regionId.split('-')
  return { contig, start: parseInt(start, 10), stop: parseInt(stop, 10) }
}

// Tab components for table switching
const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid ${(props) => props.theme.border};
  margin-bottom: 16px;
  margin-top: 10px;
`

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  color: ${({ $active, theme }) => ($active ? '#262262' : theme.textMuted)};
  cursor: pointer;
  position: relative;
  transition: color 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ $active }) => ($active ? '#262262' : 'transparent')};
    transition: background 0.15s ease;
  }

  &:hover {
    color: ${({ $active, theme }) => ($active ? '#262262' : theme.text)};
  }
`

type TableTab = 'geneBurden' | 'variants'

// Main Component Props
type LocusPageLayoutProps = {
  geneModels: GeneModels[]
  geneAssociations: GeneAssociations[]
  analysisMetadata?: AnalysisMetadata
  ancestryGroup: AncestryGroupCodes
  variantDatasets: VariantDataset[]
  variantId: string
  queryStates: any
  /** Optional locus plot data for PNG-based rendering */
  locusPlotData?: LocusPlotResponse | null
  /** Overlay data for server-rendered region view */
  regionOverlay?: RegionOverlayResponse
  /** Whether this is a large region using server-side rendering */
  isLargeRegion?: boolean
  /** Effective render context; URL provenance remains in Recoil untouched. */
  context: GenomicContext
  /** Flow within a parent document instead of owning a full-height scroller. */
  embedded?: boolean
}

// Main Component
const LocusPageLayoutComponent: React.FC<LocusPageLayoutProps> = ({
  geneModels,
  geneAssociations,
  ancestryGroup,
  analysisMetadata,
  variantDatasets,
  queryStates,
  locusPlotData,
  regionOverlay,
  isLargeRegion,
  context,
  embedded = false,
}) => {
  const [membershipFilters, setMembershipFilters] = useRecoilState(membershipFiltersAtom)
  const variantDetails = useRecoilValue(multiAnalysisVariantDetailsAtom)
  const variantId = useRecoilValue(variantIdAtom)
  const filter = useRecoilValue(variantFilterAtom)
  const [searchText, setVariantSearchText] = useRecoilState(variantSearchTextAtom)
  const [sortState, setSortState] = useRecoilState(sortStateAtom)
  const tableFormat = useRecoilValue(multiAnalysisVariantTableFormatAtom)
  const gwasCatalogOption = useRecoilValue(gwasCatalogOptionsAtom)
  const regionId = context.kind === 'locus' ? context.regionId : null
  const [activeTab, setActiveTab] = useState<TableTab>('geneBurden')
  const locusMaf = useRecoilValue(locusMafAtom)
  const geneId = context.kind === 'gene' ? context.geneId : null
  const { goToGene, goToLocus } = useAppNavigation()
  const regionViewerSlotRef = React.useRef<HTMLDivElement>(null)
  const [regionViewerWidth, setRegionViewerWidth] = React.useState(0)

  React.useEffect(() => {
    const slot = regionViewerSlotRef.current
    if (!slot) return undefined

    const updateWidth = (measuredWidth: number) => {
      setRegionViewerWidth(normalizeRegionViewerWidth(measuredWidth))
    }

    updateWidth(slot.clientWidth)
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      updateWidth(entry?.contentRect.width ?? slot.clientWidth)
    })
    observer.observe(slot)

    return () => observer.disconnect()
  }, [])

  const geneModel = !regionId ? geneModels[0] : undefined
  const exons = geneModel?.exons.filter((e) => e.feature_type === 'CDS')

  // Check if the selected variant falls outside the gene's CDS exon regions.
  // If so, use a continuous locus view so the variant is visible in context.
  const selectedVariantPos = React.useMemo(() => {
    if (!variantId || !variantDatasets) return null
    for (const vds of variantDatasets) {
      const v = findVariantById(vds.data, variantId)
      if (v?.locus?.position) return v.locus.position
    }
    return null
  }, [variantId, variantDatasets])

  const variantInExons = React.useMemo(() => {
    if (!selectedVariantPos || !exons || exons.length === 0) return true
    // Check if variant position falls within any exon (with small padding)
    const padding = 100
    return exons.some(e => selectedVariantPos >= e.start - padding && selectedVariantPos <= e.stop + padding)
  }, [selectedVariantPos, exons])

  const regions = regionId
    ? (() => {
      const { start, stop } = parseRegionId(regionId)
      return [
        {
          feature_type: 'region',
          start: start - 50000,
          stop: stop + 50000,
          previousRegionDistance: 0,
          offset: 0,
        },
      ]
    })()
    : (variantId && !variantInExons && geneModel)
      ? [{
        feature_type: 'region',
        start: geneModel.start - 10000,
        stop: geneModel.stop + 10000,
        previousRegionDistance: 0,
        offset: 0,
      }]
      : (exons && exons.length > 0) ? exons : [{ start: 0, stop: 100 }]

  let Container = PageWithGeneBurdenDetails

  if (variantDetails) {
    Container = PageWithVariantDetails
  }


  const sortAndFilterVariants = (variants: VariantJoined[]) =>
    sortItems(filterVariants(variants, { ...filter, searchText }, membershipFilters), {
      sortKey: sortState.sortKey,
      sortOrder: sortState.sortOrder,
    })

  // Compute AN and AF fields from metadata where source data is missing
  const enrichVariantWithMetadata = (variant: VariantJoined): VariantJoined => {
    const v = { ...variant } as any
    const nCases = analysisMetadata?.n_cases
    const nControls = analysisMetadata?.n_controls

    // AN Case = n_cases * 2 (diploid)
    if ((v.an_cases === null || v.an_cases === undefined) && nCases) {
      v.an_cases = nCases * 2
    }
    // AN Controls = n_controls * 2
    if ((v.an_controls === null || v.an_controls === undefined) && nControls) {
      v.an_controls = nControls * 2
    }
    // AF Case = ac_cases / an_cases
    if ((v.af_cases === null || v.af_cases === undefined) && v.ac_cases != null && nCases) {
      const anCases = nCases * 2
      if (anCases > 0) v.af_cases = v.ac_cases / anCases
    }
    // AF Controls = ac_controls / an_controls
    if ((v.af_controls === null || v.af_controls === undefined) && v.ac_controls != null && nControls) {
      const anControls = nControls * 2
      if (anControls > 0) v.af_controls = v.ac_controls / anControls
    }
    // AN Trait = association_ac / association_af
    if ((v.association_an === null || v.association_an === undefined) && v.association_ac != null && v.association_af != null && v.association_af > 0) {
      v.association_an = Math.round(v.association_ac / v.association_af)
    }
    return v as VariantJoined
  }

  const datasets: VariantJoined[][] = React.useMemo(() => {
    if (!variantDatasets) return [[]]

    const allFiltered: VariantJoined[] = variantDatasets
      .filter((vds) => vds.ancestryGroup === ancestryGroup)
      .flatMap((vds) => {
        const effectiveSearchText = variantId ? '' : searchText
        const sourceVariants = vds.data.map(enrichVariantWithMetadata)
        let filtered = filterVariants(sourceVariants, { ...filter, searchText: effectiveSearchText }, membershipFilters)

        if (regionId) {
          const { start, stop } = parseRegionId(regionId);
          filtered = filtered.filter((variant) => {
            const variantPos = variant.locus?.position;
            if (variantPos === undefined) return false;
            return variantPos >= start && variantPos <= stop;
          });
        } else if (geneModel) {
          const { start, stop } = geneModel;
          filtered = filtered.filter((variant) => {
            const variantPos = variant.locus?.position;
            if (variantPos === undefined) return false;
            return variantPos >= start && variantPos <= stop;
          });
        }

        if (gwasCatalogOption === 'filter') {
          filtered = filtered.filter((v) => v.gwas_catalog)
        }

        return includeSelectedVariant(filtered, sourceVariants, variantId)
      })

    // Deduplicate across all sequencing types — outer merge can produce
    // the same variant from both exome and genome; prefer the record with association data.
    const seen = new Map<string, VariantJoined>()
    for (const v of allFiltered) {
      const identity = normalizeVariantId(v.variant_id)
      const existing = seen.get(identity)
      if (!existing || (v.pvalue != null && existing.pvalue == null)) {
        seen.set(identity, v)
      }
    }

    return [sortItems(Array.from(seen.values()), sortState)]
  }, [variantDatasets, ancestryGroup, variantId, searchText, filter, membershipFilters, regionId, geneModel, gwasCatalogOption, sortState])

  const handleSort = (newSortKey: string) => {
    const newSortOrder: 'ascending' | 'descending' =
      newSortKey === sortState.sortKey && sortState.sortOrder === 'ascending'
        ? 'descending'
        : 'ascending'
    setSortState({ sortKey: newSortKey, sortOrder: newSortOrder })
  }

  // Clear search text when a variant is selected so it doesn't filter the plot
  React.useEffect(() => {
    if (variantId) {
      setVariantSearchText('')
    }
  }, [variantId, setVariantSearchText])

  // Create a separate dataset for tables: when a variant is selected,
  // tables show only that variant while the plot shows the full locus context.
  // Collect all matches across datasets, then deduplicate — the same variant
  // can appear in both exome and genome datasets; prefer the one with pvalue.
  const tableDatasets = React.useMemo(() => {
    if (!variantId) return datasets;
    const allMatches = datasets.flatMap(ds => ds.filter(v => variantIdsEqual(v.variant_id, variantId)));
    const seen = new Map<string, VariantJoined>();
    for (const v of allMatches) {
      const identity = normalizeVariantId(v.variant_id);
      const existing = seen.get(identity);
      if (!existing || (v.pvalue != null && existing.pvalue == null)) {
        seen.set(identity, v);
      }
    }
    const deduped = Array.from(seen.values());
    return [deduped];
  }, [datasets, variantId]);

  const hasSelectedVariantRow = Boolean(
    variantId && tableDatasets.some((dataset) =>
      dataset.some((variant) => variantIdsEqual(variant.variant_id, variantId))
    )
  )

  const renderTitle = () => {
    if (!regionId) {
      if (!geneModel) {
        return null
      }

      return (
        <>
          <h3 className="app-section-title">
            <strong>{geneModel.symbol}</strong> single variant associations with{' '}
            <strong>
              {analysisMetadata && analysisMetadata.description}
            </strong>{' '}

          </h3>
          <div className="grid-area grid-area-plot-controls">
            <Button
              onClick={() => {
                if (geneModel) {
                  const newRegionId = `${geneModel.chrom}-${geneModel.start - 200000}-${geneModel.stop + 200000}`
                  goToLocus(newRegionId, { geneId: geneId || undefined, fromPhenotype: true })
                }
              }}
            >
              Show Region
            </Button>
            <LoadingSpinners queryStates={queryStates} />
          </div>
        </>
      )
    }

    return (
      <h3 className="app-section-title">
        Single variant <strong>{variantId}</strong> associations in locus {regionId} with{' '}
        <strong>{analysisMetadata && analysisMetadata.description} {' '}</strong>

      </h3>
    )
  }

  const regionMacColumnMode = geneMacColumnMode(analysisMetadata?.trait_type)
  const regionMacColumns = regionMacColumnMode === 'case-control'
    ? ['mac_case', 'mac_control']
    : regionMacColumnMode === 'total' ? ['mac_total'] : []

  const geneResultsColKeys = [
    'gene_name_phenotype_page',
    // 'chrom',
    // 'position',
    'annotation',
    // 'ancestry_group',
    'pvalue',
    'pvalue_skat',
    'pvalue_burden',
    ...regionMacColumns,
    ...(hasGeneEffectEstimate(ancestryGroup) ? ['beta_burden'] : ['burden_direction']),
    'show',
  ]

  const handleGeneClick = (geneId: string) => {
    goToGene(geneId, { fromPhenotype: true })
  }

  const geneResultsColumns = getColumns({
    columnList: geneResultsColKeys,
    onClickGeneId: handleGeneClick,
    burdenSet: "pLoF",
  })

  const geneResultsExportColumns = getColumns({
    columnList: geneResultsColKeys.filter((key) => key !== 'show'),
    onClickGeneId: handleGeneClick,
    burdenSet: "pLoF",
  })

  const LoadingSpinners = ({ queryStates }: { queryStates: Record<string, QueryState<any>> }) => {
    const [dismissedErrors, setDismissedErrors] = React.useState<Record<string, boolean>>({});

    const dismissError = (key: string) => {
      setDismissedErrors(prev => ({ ...prev, [key]: true }));
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 50, alignItems: 'flex-end', zIndex: 10 }}>
        {Object.entries(queryStates).map(([key, state]) => {
          if ((state as QueryState<any>).error && !dismissedErrors[key]) {
            return (
              <div key={key} style={{ flexBasis: '100%', color: '#f44336', padding: '8px', borderRadius: '4px', backgroundColor: '#fff5f5', border: '1px solid #f44336', margin: '4px 0', position: 'relative' }}>
                <strong>Error loading:</strong> {key.replace(/([A-Z])/g, ' $1').toLowerCase()} - {state.error?.message}
                <span
                  style={{ marginLeft: '10px', cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => {
                    dismissError(key);
                    delete queryStates[key];
                  }}
                >X</span>
              </div>
            );
          }
          if ((state as QueryState<any>).isLoading) {
            return (
              <TinySpinner key={key} style={{ flexBasis: '100%' }}>
                Loading {key.replace(/([A-Z])/g, ' $1').toLowerCase()}...
              </TinySpinner>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <Container $embedded={embedded}>
      <div className="associations-page-grid">
        {!variantId && !regionId && (
          <>

            <div className="grid-area grid-area-burden-title">
              <h3 className="app-section-title">
                <strong>{geneModel?.symbol || variantId}</strong> gene burden associations with{' '}
                <strong>
                  {analysisMetadata && analysisMetadata.description}
                </strong>{' '}

              </h3>
            </div>
            <div className="grid-area grid-area-burden-table">
              <GeneBurdenTable
                geneAssociations={(geneAssociations).filter(d => d.ancestry_group == ancestryGroup)}
                membershipFilters={membershipFilters}
                setMembershipFilters={setMembershipFilters}
                analysisMetadata={analysisMetadata}
              />
            </div>
          </>
        )}

        <div className="grid-area grid-area-sva-title">{renderTitle()}</div>

        {regionId && (
          <div className="grid-area grid-area-plot-controls">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              {context.kind === 'locus' && context.source === 'explicit' && <ZoomRegion />}
            </div>
            <LoadingSpinners queryStates={queryStates} />
          </div>
        )}

        <div ref={regionViewerSlotRef} className="grid-area grid-area-region-viewer">
          {regionViewerWidth > 0 && (
            <RegionViewer
              leftPanelWidth={50}
              width={regionViewerWidth}
              padding={25}
              regions={regions}
              rightPanelWidth={40}
            >
              <>
                <LocusPagePlots
                  variantDatasets={datasets}
                  locusPlotData={locusPlotData}
                  regionOverlay={regionOverlay}
                  isLargeRegion={isLargeRegion}
                  context={context}
                />
                {regions && <GenesTrackContainer geneModelsInRegion={geneModels} geneAssociations={geneAssociations} locusMaf={locusMaf} />}
                <div style={{ height: 8 }} />
                <PositionAxisTrack />
              </>
            </RegionViewer>
          )}
        </div>

        {variantDetails && (
          <div className="grid-area grid-area-variant-details">
            <VariantDetails />
          </div>
        )}

        <div className="grid-area grid-area-variant-table">
          {variantId && (
            <div
              data-selected-variant-table-row={hasSelectedVariantRow ? variantId : undefined}
              role="status"
              style={{
                marginBottom: 8,
                padding: '8px 10px',
                borderLeft: '4px solid #c62828',
                background: 'var(--theme-surface-alt, #f5f5f5)',
              }}
            >
              {hasSelectedVariantRow ? (
                <>Selected variant <strong>{variantId}</strong></>
              ) : (
                <>Selected variant table data unavailable for <strong>{variantId}</strong></>
              )}
            </div>
          )}
          {regionId ? (
            <>
              <TabContainer>
                <Tab $active={activeTab === 'geneBurden'} onClick={() => setActiveTab('geneBurden')}>
                  Gene Burden
                </Tab>
                <Tab $active={activeTab === 'variants'} onClick={() => setActiveTab('variants')}>
                  Variants
                </Tab>
              </TabContainer>

              {activeTab === 'variants' ? (
                tableFormat === 'wide' && variantId && tableDatasets?.[0]?.length === 1 ? (
                  <>
                    <IndividualVariantResultsTable
                      variantDatasets={tableDatasets}
                      sortVariants={sortAndFilterVariants}
                    />
                    {tableDatasets[0][0].gwas_catalog && (
                      <GwasCatalogDetails gwasCatalog={tableDatasets[0][0].gwas_catalog} />
                    )}
                  </>
                ) : (
                  <GenePageVariantTable
                    variantDatasets={tableDatasets}
                    onSort={handleSort}
                    sortState={sortState}
                    sortVariants={sortAndFilterVariants}
                  />
                )
              ) : (
                <GeneResultsTable
                  columns={geneResultsColumns}
                  results={(() => {
                    // Filter gene associations by region bounds, ancestry, and MAF
                    const { contig, start, stop } = parseRegionId(regionId)
                    const contigWithoutChr = contig.replace(/^chr/, '')

                    const filtered = geneAssociations.filter((gene) => {
                      // Filter by ancestry
                      if (gene.ancestry_group !== ancestryGroup) return false

                      // Filter by MAF
                      if (gene.max_maf !== locusMaf) return false

                      // Filter by region - check if gene overlaps with region
                      const geneContig = gene.contig || ''
                      const geneContigNormalized = geneContig.replace(/^chr/, '')
                      if (geneContigNormalized !== contigWithoutChr) return false

                      // Use gene_start_position for filtering
                      const geneStart = gene.gene_start_position
                      if (geneStart === null || geneStart === undefined) return false

                      // Gene is in region if its start position falls within bounds
                      return geneStart >= start && geneStart <= stop
                    })

                    // Sort by pvalue (most significant first)
                    return filtered.sort((a, b) =>
                      (a.pvalue ?? 1) - (b.pvalue ?? 1)
                    )
                  })()}
                  exportColumns={geneResultsExportColumns}
                  analysisId={analysisMetadata?.analysis_id || ""}
                  burdenSet='pLoF'
                  highlightText=''
                  numRowsRendered={16}
                />
              )}
            </>
          ) : (
            tableFormat === 'wide' && variantId && tableDatasets?.[0]?.length === 1 ? (
              <>
                <IndividualVariantResultsTable
                  variantDatasets={tableDatasets}
                  sortVariants={sortAndFilterVariants}
                />
                {tableDatasets[0][0].gwas_catalog && (
                  <GwasCatalogDetails gwasCatalog={tableDatasets[0][0].gwas_catalog} />
                )}
              </>
            ) : (
              <GenePageVariantTable
                variantDatasets={tableDatasets}
                onSort={handleSort}
                sortState={sortState}
                sortVariants={sortAndFilterVariants}
              />
            )
          )}
        </div>
      </div>
    </Container>
  )
}

export const LocusPageLayout = LocusPageLayoutComponent
